/**
 * useSavedAppointmentFocus.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Depois de salvar um agendamento no celular: leva a agenda até o dia dele,
 * destaca o cartão por alguns segundos e avisa "Agendado para dd/MM às HH:mm".
 *
 * O AppointmentDrawer fecha ao salvar, por isso o aviso mora na página (mesmo
 * padrão de useAgendaWhatsAppSend, que devolve o `toast` pronto para render).
 *
 * @module useSavedAppointmentFocus
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Toast from '../../../../components/support/Toast';

const HIGHLIGHT_MS = 5000;
const NOTICE_MS = 4000;

const pad = (n) => String(n).padStart(2, '0');

/**
 * @param {Object}   params
 * @param {Function} params.goToDate - Leva a visão atual até a data (Date) informada
 * @returns {{ onSaved: Function, highlighted: Object|null, toast: JSX.Element }}
 *   onSaved recebe o objeto que o AppointmentDrawer passa em onSave
 *   (date "YYYY-MM-DD HH:MM:SS", patient, id quando a API devolve).
 */
export function useSavedAppointmentFocus({ goToDate }) {
  const [notice, setNotice] = useState(null);
  const [highlighted, setHighlighted] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const onSaved = useCallback((saved) => {
    // Recorrência e outros caminhos sem data: nada a destacar
    if (!saved?.date) return;
    const when = new Date(saved.date);
    if (Number.isNaN(when.getTime())) return;

    goToDate(when);
    setNotice(`Agendado para ${pad(when.getDate())}/${pad(when.getMonth() + 1)} às ${pad(when.getHours())}:${pad(when.getMinutes())}`);
    setHighlighted({ id: saved.id ?? null, date: saved.date, patient: saved.patient ?? null });

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHighlighted(null), HIGHLIGHT_MS);
  }, [goToDate]);

  const toast = (
    <Toast
      open={!!notice}
      message={notice || ''}
      severity="success"
      autoHideDuration={NOTICE_MS}
      onClose={() => setNotice(null)}
    />
  );

  return { onSaved, highlighted, toast };
}
